import { useContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box, Button, Stack } from '@mui/material'
import { TrendingFlat, OpenInNew } from '@mui/icons-material'
import FairCarousel from '../../components/FairCarousel'
import { config } from '../../constants'
import SeoHome from '../../components/seo/SeoHome'
import { useDispatch, useSelector } from 'react-redux'
import { IReducer } from '../../interface'
import InternationalEvents from './InternationalEvents'
import Rhino8LaunchPromotion from './Rhino8LaunchPromotion'
import Block2Col from '../../components/Common/Block2Col'
import BlockFull2Col from '../../components/Common/BlockFull2Col'
import { ConfigContext } from '../../App'
import { getBannersGQL } from 'src/services/graphql/schemas/banners'
import { getNotificationsGQL } from 'src/services/graphql/schemas/notifications'
import HeroHome from './HeroHome'

const Home = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const banners = useSelector((state: IReducer) => state.dataBannersReducer)
  const notifications = useSelector((state: IReducer) => state.notificationsReducer)
  const configContext = useContext(ConfigContext)

  useEffect(() => {
    if (!banners) {
      dispatch(getBannersGQL())
    }
  }, [banners])

  useEffect(() => {
    if (!notifications) {
      dispatch(getNotificationsGQL())
    }
  }, [notifications])

  return (
    <div data-cy='home-container'>
      <SeoHome
        title={intl.formatMessage({ id: 'seo.home.title' })}
        description={intl.formatMessage({ id: 'seo.home.description' })}
      />
      <FairCarousel items={notifications} />
      <FairCarousel items={banners} />
      <HeroHome />
      <BlockFull2Col
        iconImage='https://gemvision.com/Images/MatrixGold/MatrixGold%20Light%20Registered.svg'
        altIcon='MatrixGold'
        title='home.matrixgoldMasterCertification.title'
        image='https://meteor.stullercloud.com/das/141601566?wid=1600'
        description={['home.matrixgoldMasterCertification.description']}
        alt='Ring with accent diamonds and central emerals with a velvet background.'
      >
        <Button
          data-gtm-primary-cta='Buy_Now_Master_Certification_Block'
          variant='contained'
          color='primary'
          component={Link}
          to='/store/matrixgold-instructorled-class/matrixgold-master-certification-level-3-in-person'
          endIcon={<TrendingFlat />}
          data-cy='home-banner-button'
        >
          <FormattedMessage id='common.buyNow' />
        </Button>
      </BlockFull2Col>
      <BlockFull2Col
        iconImage='https://meteor.stullercloud.com/das/136871401?fmt=smart-alpha&hei=64'
        altIcon='MatrixGold and CounterSketch'
        squareIcon
        title='learning.memberships'
        inverted
        description={['home.banner.membership.description']}
        image='https://meteor.stullercloud.com/das/139489438?wid=1080'
        imageCompare='https://meteor.stullercloud.com/das/139489439?wid=1080'
        alt='Ring with a row of central princess cut sapphires and accent diamonds on a metal stand.'
        altCompare='Shaded view of a ring with a row of central princess cut sapphires and accent diamonds on a metal stand.'
        noPaddingTop
      >
        <Button
          data-gtm-third-cta='Matrixgold_Membership'
          variant='outlined'
          color='secondary'
          component={Link}
          to='/matrixgold/membership'
          endIcon={<TrendingFlat />}
          data-cy='home-banner-button'
        >
          <FormattedMessage id='common.matrixGoldMembership' />
        </Button>
        <Button
          data-gtm-third-cta='Countersketch_Membership'
          variant='outlined'
          color='secondary'
          component={Link}
          to='/countersketch/membership'
          endIcon={<TrendingFlat />}
          data-cy='home-banner-button'
        >
          <FormattedMessage id='common.counterSketchMembership' />
        </Button>
      </BlockFull2Col>
      <Rhino8LaunchPromotion />
      <Box px={{ xs: 2, sm: 8, lg: 12 }} py={{ xs: 6, sm: 8 }}>
        <Stack gap={{ xs: 4, sm: 5 }} maxWidth={1200} m='auto'>
          <Block2Col
            iconImage='https://gemvision.com/Images/MatrixGold/MatrixGold%20Light%20Registered.svg'
            title='home.matrixgold.title'
            description={['home.matrixgold.description']}
            image='https://meteor.stullercloud.com/das/130395104?wid=1080'
          >
            <Button
              data-gtm-secondary-cta='Learn_More_MG_Block'
              variant={configContext.isFromUSCountry ? 'outlined' : 'contained'}
              color={configContext.isFromUSCountry ? 'secondary' : 'primary'}
              endIcon={<TrendingFlat />}
              component={Link}
              to='/matrixgold'
              data-cy='home-banner-button'
            >
              <FormattedMessage id='common.learnMore' />
            </Button>
            <Button
              data-gtm-primary-cta='Watch_Demo_MG_Block'
              variant='outlined'
              color='secondary'
              href={config.MATRIXGOLD_DEMO_GEMVISION}
              target='_blank'
              rel='noopener noreferrer'
              endIcon={<OpenInNew />}
              data-cy='home-banner-button'
            >
              <FormattedMessage id='common.watchDemo' />
            </Button>
            {configContext.isFromUSCountry && (
              <Button
                data-gtm-primary-cta='Request_Live_Demo_MG_Block'
                variant={configContext.isFromUSCountry ? 'contained' : 'outlined'}
                color='primary'
                href={config.MATRIXGOLD_LIVE_DEMO_GEMVISION}
                target='_blank'
                rel='noopener noreferrer'
                endIcon={<OpenInNew />}
                data-cy='home-banner-button'
              >
                <FormattedMessage id='common.requestLiveDemo' />
              </Button>
            )}
          </Block2Col>
          <Block2Col
            cs
            iconImage='https://gemvision.com/Images/CounterSketch/CounterSketch-Primary-Logo-1-Color-Ocean-Blue.svg'
            title='home.countersketch.title'
            description={['home.countersketch.description']}
            image='https://meteor.stullercloud.com/das/130395103?wid=1080'
            inverted
          >
            <Button
              data-gtm-secondary-cta='Learn_More_CS_Block'
              variant={configContext.isFromUSCountry ? 'outlined' : 'contained'}
              color='info'
              endIcon={<TrendingFlat />}
              component={Link}
              to='/countersketch'
              data-cy='home-banner-button'
            >
              <FormattedMessage id='common.learnMore' />
            </Button>
            <Button
              data-gtm-primary-cta='Watch_Demo_CS_Block'
              color='info'
              variant='outlined'
              href={config.COUNTERSKETCH_DEMO_GEMVISION}
              target='_blank'
              rel='noopener noreferrer'
              endIcon={<OpenInNew />}
              data-cy='home-banner-button'
            >
              <FormattedMessage id='common.watchDemo' />
            </Button>
            {configContext.isFromUSCountry && (
              <Button
                data-gtm-primary-cta='Request_Live_Demo_CS_Block'
                variant={configContext.isFromUSCountry ? 'contained' : 'outlined'}
                color='primary'
                href={config.COUNTERSKETCH_LIVE_DEMO_GEMVISION}
                target='_blank'
                rel='noopener noreferrer'
                endIcon={<OpenInNew />}
                data-cy='home-banner-button'
              >
                <FormattedMessage id='common.requestLiveDemo' />
              </Button>
            )}
          </Block2Col>
          <Block2Col
            iconImage='https://gemvision.com/Images/CounterSketch/CounterSketch-Primary-Logo-1-Color-Ocean-Blue.svg'
            title='countersketch.b9.customJewelrySolution'
            description={['countersketch.b9.customJewelrySolution.description']}
            image='https://meteor.stullercloud.com/das/130395100?wid=1080'
          >
            <Button
              data-gtm-secondary-cta='Discover_Now_Custom_Solution_Block'
              variant='outlined'
              color='secondary'
              component={Link}
              to='/countersketch/jewelry-3d-printing'
              data-cy='home-banner-button'
            >
              <FormattedMessage id='common.discoverNow' />
            </Button>
          </Block2Col>
        </Stack>
      </Box>
      <InternationalEvents />
      <BlockFull2Col
        inverted
        title='home.innovationIsOurTradition.title'
        image='https://meteor.stullercloud.com/das/132911473'
        alt='A gird in a futuristic background with a beautiful necklace.'
        videoURL='https://youtu.be/8OOaw4eySm8'
        description={['home.innovationIsOurTradition.description1', 'home.innovationIsOurTradition.description2']}
      />
    </div>
  )
}

export default Home
